/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Box, Button, Link, TextField, Typography } from '@mui/material'
import { Link as RouterLink, Navigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useMutation, useQuery } from '@tanstack/react-query'
import { signupAPI } from '../api/crm'
import { getErrorMessage } from '../utils/apiErrors'
import AuthCard from '../components/common/AuthCard'
import SsoButtons from '../components/common/SsoButtons'
import Loading from '../components/common/Loading'

/**
 * Start a free trial: Google / Microsoft (then name the workspace), or email + a verification link.
 * The answer never says whether the email already has an account (that person gets an email saying so).
 */
const SignupPage = () => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)
  const config = useQuery({ queryKey: ['signup', 'config'], queryFn: signupAPI.config, retry: false })
  const [form, setForm] = useState({ name: '', email: '', workspaceName: '', website: '' })
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))
  const start = useMutation({
    mutationFn: () => signupAPI.start({ email: form.email.trim(), name: form.name.trim() || undefined,
      workspaceName: form.workspaceName.trim(), website: form.website || undefined }),
  })

  if (isAuthenticated) return <Navigate to="/select-tenant?create=1" replace />
  if (config.isLoading) return <Loading fullScreen />
  if (!config.data?.enabled) {
    return (
      <AuthCard subtitle="Create an account">
        <Alert severity="info">Sign-up isn’t open here. Ask your administrator for an invite.</Alert>
        <Link component={RouterLink} to="/login" variant="body2" sx={{ display: 'inline-block', mt: 2 }}>Back to sign in</Link>
      </AuthCard>
    )
  }

  return (
    <AuthCard subtitle="Start your free trial">
      {start.isSuccess ? (
        <Alert severity="success">
          Check your inbox: we’ve sent a link to <strong>{form.email.trim()}</strong> to confirm your email and choose a password. It works for 24 hours.
        </Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            14 days free, no card needed. Your workspace starts empty or from a ready-made template.
          </Typography>
          {config.data.emailSignup && (
            <Box component="form" onSubmit={(e) => { e.preventDefault(); start.mutate() }} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {start.error && <Alert severity="error">{getErrorMessage(start.error)}</Alert>}
              <TextField label="Your name" autoComplete="name" value={form.name} onChange={set('name')} inputProps={{ maxLength: 100 }} />
              <TextField label="Work email" type="email" autoComplete="email" required value={form.email} onChange={set('email')} />
              <TextField label="Company or workspace name" required value={form.workspaceName} onChange={set('workspaceName')} inputProps={{ maxLength: 100 }} />
              {/* hidden from people; bots fill it in */}
              <Box sx={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
                <TextField label="Website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
              </Box>
              <Button type="submit" variant="contained" disabled={start.isPending}>{start.isPending ? 'Sending…' : 'Start free trial'}</Button>
            </Box>
          )}
          <SsoButtons next="/select-tenant?create=1" dividerLabel={config.data.emailSignup ? 'or' : 'sign up with'} />
        </>
      )}
      <Typography variant="body2" sx={{ mt: 2 }}>
        Already have an account? <Link component={RouterLink} to="/login">Sign in</Link>
      </Typography>
    </AuthCard>
  )
}

export default SignupPage
